import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const AddBrandPage = () => {
  const navigate = useNavigate();
  const { updateOnboarding, loadUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    slug: '',
    legalName: '',

    // Company Details
    description: '',
    foundedYear: '',
    industry: '',
    website: '',

    // Primary Contact / Brand Owner
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    ownerTitle: '',

    // Operations Contact
    opsContactName: '',
    opsContactEmail: '',
    opsContactPhone: '',

    // Finance/Billing Contact
    financeContactName: '',
    financeContactEmail: '',
    financeContactPhone: '',

    // Business Address
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',

    // Social Media
    instagramHandle: '',
    tiktokHandle: '',

    // Settings
    currency: 'USD',
    timezone: 'America/New_York',

  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Auto-generate slug from name
    if (name === 'name' && !formData.slug) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData(prev => ({
        ...prev,
        slug,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.slug) {
      toast.error('Please fill in brand name and slug');
      return;
    }

    if (!formData.ownerName || !formData.ownerEmail) {
      toast.error('Please provide primary contact information');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        slug: formData.slug,
        legalName: formData.legalName || formData.name,
        profile: {
          description: formData.description,
          foundedYear: formData.foundedYear ? parseInt(formData.foundedYear) : undefined,
          industry: formData.industry,
          website: formData.website,
          socialMedia: {
            instagram: formData.instagramHandle,
            tiktok: formData.tiktokHandle,
          },
        },
        contacts: {
          owner: {
            name: formData.ownerName,
            email: formData.ownerEmail,
            phone: formData.ownerPhone,
            title: formData.ownerTitle,
          },
          operations: formData.opsContactName ? {
            name: formData.opsContactName,
            email: formData.opsContactEmail,
            phone: formData.opsContactPhone,
          } : undefined,
          finance: formData.financeContactName ? {
            name: formData.financeContactName,
            email: formData.financeContactEmail,
            phone: formData.financeContactPhone,
          } : undefined,
        },
        address: formData.addressLine1 ? {
          line1: formData.addressLine1,
          line2: formData.addressLine2,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country,
        } : undefined,
        settings: {
          currency: formData.currency,
          timezone: formData.timezone,
        },
        status: 'pending_setup',
      };

      const response = await axios.post('/api/brands', payload);

      if (response.data.success) {
        // Refresh user data to include the newly created brand
        await loadUser();

        const onboardingResult = await updateOnboarding({
          status: 'brand_created',
          currentStep: 'financial_plan'
        });

        if (!onboardingResult.success) {
          toast.error(onboardingResult.error);
        }

        toast.success("Brand created! Now let's build your financial plan.");
        navigate('/get-started');
      }
    } catch (error) {
      console.error('Error creating brand:', error);
      toast.error(error.response?.data?.error || 'Failed to create brand');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/brands')}
          sx={{ mb: 2 }}
        >
          Back to Brands
        </Button>
        <Typography variant="h4" gutterBottom>
          Add New Brand
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Create a new brand on Studio Sivad
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Brand Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  helperText="Public-facing brand name"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  helperText="URL-friendly identifier (auto-generated)"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Legal Name"
                  name="legalName"
                  value={formData.legalName}
                  onChange={handleChange}
                  helperText="Official legal entity name (if different from brand name)"
                />
              </Grid>

              {/* Company Details */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Company Details
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Brand Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  helperText="Brief description of the brand, its mission, and target audience"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Founded Year"
                  name="foundedYear"
                  type="number"
                  value={formData.foundedYear}
                  onChange={handleChange}
                  placeholder="2024"
                  helperText="Year the company was founded"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  placeholder="Streetwear, Luxury, etc."
                  helperText="Primary fashion category"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://brand.com"
                  helperText="Brand website URL"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Instagram Handle"
                  name="instagramHandle"
                  value={formData.instagramHandle}
                  onChange={handleChange}
                  placeholder="@brandname"
                  helperText="Instagram username"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="TikTok Handle"
                  name="tiktokHandle"
                  value={formData.tiktokHandle}
                  onChange={handleChange}
                  placeholder="@brandname"
                  helperText="TikTok username"
                />
              </Grid>

              {/* Primary Contact / Owner */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Primary Contact / Brand Owner *
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Main point of contact for the brand
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Full Name"
                  name="ownerName"
                  value={formData.ownerName}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Title"
                  name="ownerTitle"
                  value={formData.ownerTitle}
                  onChange={handleChange}
                  placeholder="Founder, CEO, Creative Director, etc."
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Email"
                  name="ownerEmail"
                  type="email"
                  value={formData.ownerEmail}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="ownerPhone"
                  type="tel"
                  value={formData.ownerPhone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                />
              </Grid>

              {/* Operations Contact */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Operations Contact (Optional)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Day-to-day operations and campaign management
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="opsContactName"
                  value={formData.opsContactName}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Email"
                  name="opsContactEmail"
                  type="email"
                  value={formData.opsContactEmail}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="opsContactPhone"
                  type="tel"
                  value={formData.opsContactPhone}
                  onChange={handleChange}
                />
              </Grid>

              {/* Finance Contact */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Finance / Billing Contact (Optional)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Invoicing, payments, and financial matters
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="financeContactName"
                  value={formData.financeContactName}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Email"
                  name="financeContactEmail"
                  type="email"
                  value={formData.financeContactEmail}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="financeContactPhone"
                  type="tel"
                  value={formData.financeContactPhone}
                  onChange={handleChange}
                />
              </Grid>

              {/* Business Address */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Business Address (Optional)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Physical or mailing address for official correspondence
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleChange}
                  placeholder="Street address"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  placeholder="Suite, unit, building, etc."
                />
              </Grid>

              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="State / Province"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="ZIP / Postal Code"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                />
              </Grid>

              {/* Settings */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Settings
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    label="Currency"
                  >
                    <MenuItem value="USD">USD - US Dollar</MenuItem>
                    <MenuItem value="EUR">EUR - Euro</MenuItem>
                    <MenuItem value="GBP">GBP - British Pound</MenuItem>
                    <MenuItem value="CAD">CAD - Canadian Dollar</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Timezone</InputLabel>
                  <Select
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleChange}
                    label="Timezone"
                  >
                    <MenuItem value="America/New_York">Eastern Time</MenuItem>
                    <MenuItem value="America/Chicago">Central Time</MenuItem>
                    <MenuItem value="America/Denver">Mountain Time</MenuItem>
                    <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Actions */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/brands')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Brand'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AddBrandPage;
